<template>
  <div
    class="undo-strip"
    @focusin="enter"
    @focusout="release"
    @pointerenter="hold(true)"
    @pointerleave="hold(false)"
    @pointerdown="hold(true)"
  >
    <span class="text">{{ text }}</span>
    <!-- The count and «Вернуть» go together: under the words, at the right, when the strip is narrow. -->
    <span class="tail">
      <span class="count" aria-hidden="true">{{ left }}</span>
      <AppButton
        ref="button"
        variant="tinted"
        class="restore"
        :busy="busy"
        :busy-label="busyLabel"
        :inactive="waiting || inactive"
        @click="restore"
      >
        <template #icon><IconUndo /></template>
        {{ waiting ? waitingAction : action }}
      </AppButton>
    </span>
  </div>
</template>

<script lang="ts">
import { defineComponent, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import type { ComponentPublicInstance } from 'vue'
import IconUndo from '~icons/mdi/undo-variant'
import AppButton from '@/components/AppButton.vue'
import { useAnnouncer } from '@/composables/useAnnouncer'

/**
 * «Удалено · Вернуть» (MOL-82, handoff 02): what was removed without a question, and ten seconds
 * to take it back. The count stands still while a finger rests on the strip or the focus is in
 * it — a person reaching for the button must not lose it on the way. Shared: «Обмен денег» and
 * «Доходы» are the next to take it.
 *
 * One look and one place (Ф-29): the words, the count in a ring of `--graphic`, «Вернуть» — and a
 * screen puts it in `AppScreen`'s `#undo`, 8 over the strip; a sheet, in its footer. The count is
 * not read out: the announcement says the ten seconds once, and a live region ticking under the
 * person reaching for the button would talk over them.
 *
 * The words are said once as the strip comes; the focus goes to «Вернуть», since the row it was
 * opened from is gone. What happens after — back or final — is the screen's to say.
 *
 * A «Вернуть» that waits for the server — an exchange's, an income's (MOL-189) — says so: at work it
 * is `busy` with its word, and with no connection not now, saying why (Ф-6). The count stands still
 * while it cannot be pressed (owner's В-2 «а»): ten seconds are the time to take it back, and run out
 * offline they took away a removal the server still keeps for ten minutes.
 */
export default defineComponent({
  name: 'UndoStrip',
  components: { AppButton, IconUndo },
  props: {
    /** «Удалено: барбер · 5 000 ֏» — what the strip shows. */
    text: { type: String, required: true },
    /** What is said as it comes: the text and how long there is to take it back. */
    announcement: { type: String, required: true },
    action: { type: String, required: true },
    seconds: { type: Number, default: 10 },
    /**
     * Shown again — on the next screen, the same removal: the words were said and the focus given
     * the first time, and taking them again moved the person's focus for a thing already known.
     */
    quiet: { type: Boolean, default: false },
    /** «Вернуть» at work — the server is asked — with the word that says so (MOL-225). */
    busy: { type: Boolean, default: false },
    busyLabel: { type: String, default: '' },
    /** «Вернуть» cannot be pressed now — no connection — and says why in `waitingAction`. */
    waiting: { type: Boolean, default: false },
    waitingAction: { type: String, default: '' },
    /**
     * Another write of the screen is in the air (MOL-189, adversarial А2): its answer makes the removal
     * final, and a «Вернуть» pressed meanwhile would be sent nowhere — not now, with its own word.
     */
    inactive: { type: Boolean, default: false },
  },
  /** `tick`: what is left, every second, held or not — a screen taking the strip over goes on from it. */
  emits: ['restore', 'expire', 'tick'],
  setup(props, { emit }) {
    const left = ref(props.seconds)
    const button = ref<ComponentPublicInstance | null>(null)
    let held = false
    let focused = false
    let timer: ReturnType<typeof setInterval> | undefined
    let unsay: (() => void) | undefined
    const announce = useAnnouncer()

    function tick(): void {
      if (!held && !focused && !props.waiting && !props.busy && !props.inactive) left.value -= 1
      emit('tick', left.value)
      if (left.value <= 0) {
        clearInterval(timer)
        emit('expire')
      }
    }

    function hold(value: boolean): void {
      held = value
    }

    /**
     * The focus the strip puts on «Вернуть» itself does not stop the count: it would stand still
     * until the person tapped somewhere else, with «Трата» hidden under it all that time. Only a
     * focus the person brought here does.
     */
    let placing = false
    function enter(): void {
      if (!placing) focused = true
    }

    function release(event: FocusEvent): void {
      const next = event.relatedTarget
      focused = next instanceof Node && (event.currentTarget as Element).contains(next)
    }

    function restore(): void {
      clearInterval(timer)
      emit('restore')
    }

    // A «Вернуть» that waited for the server and did not get it back leaves the strip: the count goes
    // on from where it stood, or the strip stayed for good, its ten seconds stopped (MOL-189 №2).
    watch(
      () => props.busy,
      (busy, was) => {
        if (busy || !was) return
        clearInterval(timer)
        timer = setInterval(tick, 1000)
      },
    )

    onMounted(async () => {
      timer = setInterval(tick, 1000)
      if (props.quiet) return
      unsay = announce?.(props.announcement)
      await nextTick()
      const element = button.value?.$el as HTMLElement | undefined
      placing = true
      element?.focus()
      placing = false
    })
    onBeforeUnmount(() => {
      clearInterval(timer)
      unsay?.()
    })

    return { left, button, hold, enter, release, restore }
  },
})
</script>

<style scoped lang="scss">
// The words beside the count and «Вернуть» while they have 7rem; narrower, the words take the first
// line and the two go under them together, at the right. Kept on one line, a «Вернуть» holding «Возвращаем…»
// left the words some 50 px at 320 and broke them a letter a line, and «Вернём, когда будет связь»
// none at all (MOL-189).
.undo-strip {
  @include appear;

  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-3);
  min-height: 3.5rem;
  padding: var(--space-2) var(--space-2) var(--space-2) var(--space-3);
  border: var(--hairline) solid var(--border);
  border-radius: var(--radius-lg);
  background: var(--surface);
  box-shadow: var(--shadow-md);
}

.tail {
  display: flex;
  flex: 0 1 auto;
  align-items: center;
  gap: var(--space-3);
  min-width: 0;
  margin-left: auto;
}

.count {
  display: grid;
  flex: none;
  place-items: center;
  width: 1.75rem;
  height: 1.75rem;
  border-radius: var(--radius-pill);
  box-shadow: inset 0 0 0 2px var(--graphic);
  color: var(--text-muted);
  font-size: var(--text-footnote);
  font-weight: var(--weight-bold);
  font-variant-numeric: tabular-nums;
}

.text {
  flex: 1 1 7rem;
  min-width: 0;
  font-size: var(--text-callout);
  overflow-wrap: anywhere;
}

// May give way, its words wrapping inside it, so a long one never stands past the strip.
.restore {
  flex: 0 1 auto;
  min-width: 0;
}
</style>
